import { useState, useEffect, useCallback } from 'react';
import { Search, Plus, X, Loader2, Phone, Mail, FileSpreadsheet, Pencil } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { clientesService } from '@/services/clientes.service';
import { useApiError } from '@/hooks/useApiError';
import { ApiErrorDisplay } from '@/components/ApiErrorDisplay';
import { dialogs } from '@/stores/dialog-store';
import { ImportCustomersModal } from './import-customers-modal';

const emptyForm = { name: '', phone: '', email: '', address: '', rfc: '', creditLimit: 0 };

export function CustomersPage() {
  const { user } = useAuthStore();
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const { error, errorType, handleApiError, handleAuthRedirect } = useApiError();

  const loadCustomers = useCallback(async () => {
    if (!user?.company?.id) return;
    setLoading(true);
    try {
      const result = await clientesService.findAll(user.company.id, search || undefined);
      const list = Array.isArray(result) ? result : (result?.data || []);
      setCustomers(list);
    } catch (err: any) { handleApiError(err); }
    setLoading(false);
  }, [user, search]);

  useEffect(() => { loadCustomers(); }, [loadCustomers]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (customer: any) => {
    setEditingId(customer.id);
    setForm({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      address: customer.address || '',
      rfc: customer.rfc || '',
      creditLimit: Number(customer.creditLimit || 0),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSubmit = async () => {
    if (!user?.company?.id || !form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone || undefined,
        email: form.email || undefined,
        address: form.address || undefined,
        rfc: form.rfc || undefined,
        creditLimit: form.creditLimit || 0,
      };

      if (editingId) {
        await clientesService.update(editingId, payload);
      } else {
        await clientesService.create({
          companyId: user.company.id,
          ...payload,
        });
      }
      closeForm();
      loadCustomers();
    } catch (err: any) { handleApiError(err); }
    setSaving(false);
  };

  const handleDeactivate = async (id: string, name: string) => {
    if (!await dialogs.confirm(`¿Desactivar cliente "${name}"?`)) return;
    try {
      await clientesService.deactivate(id);
      loadCustomers();
    } catch (err: any) { handleApiError(err); }
  };

  return (
    <div className="w-full">
      <ApiErrorDisplay error={error} errorType={errorType} onRetry={loadCustomers} onLogin={handleAuthRedirect} />
      {!error && (
      <>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold">Clientes</h1>
          <p className="text-sm text-ios-gray mt-1">{customers.length} clientes registrados</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImportModal(true)}
            className="px-3 py-2 rounded-xl text-sm font-medium border border-gray-300 hover:bg-gray-50 flex items-center gap-1.5 transition-colors"
          >
            <FileSpreadsheet size={16} className="text-green-600" />
            Importar Excel
          </button>
          <button
            onClick={openCreate}
            className="ios-button flex items-center gap-2 px-4 py-2"
          >
            <Plus size={16} />
            Nuevo Cliente
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-sm mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ios-gray" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar cliente..."
          className="ios-input w-full pl-9 h-9 text-sm"
        />
      </div>

      {/* Customers grid */}
      {loading ? (
        <div className="flex items-center justify-center h-40">
          <Loader2 size={24} className="animate-spin text-ios-gray" />
        </div>
      ) : customers.length === 0 ? (
        <div className="ios-card p-8 text-center text-ios-gray text-sm">
          No se encontraron clientes
        </div>
      ) : (
        <div className="ios-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ios-gray-5 bg-ios-gray-6/50">
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Cliente</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Contacto</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">RFC</th>
                  <th className="text-left px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Crédito</th>
                  <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Ventas</th>
                  <th className="text-right px-4 py-3 font-medium text-ios-gray text-xs uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ios-gray-5">
                {customers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-ios-gray-6/30 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-ios-blue/20 to-purple-200 rounded-full flex items-center justify-center text-ios-blue font-bold text-xs flex-shrink-0">
                          {(customer.name || '?').charAt(0).toUpperCase()}
                        </div>
                        <span className="font-semibold text-ios-dark">{customer.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-1">
                        {customer.phone && (
                          <span className="text-xs text-ios-gray flex items-center gap-1.5">
                            <Phone size={12} /> {customer.phone}
                          </span>
                        )}
                        {customer.email && (
                          <span className="text-xs text-ios-gray flex items-center gap-1.5">
                            <Mail size={12} /> {customer.email}
                          </span>
                        )}
                        {!customer.phone && !customer.email && <span className="text-xs text-ios-gray-4">—</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-ios-gray">
                      {customer.rfc || '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5 text-xs">
                        <span className="text-ios-gray">
                          Límite: <span className="font-medium text-ios-dark">${Number(customer.creditLimit).toFixed(2)}</span>
                        </span>
                        <span className="text-ios-gray">
                          Usado: <span className="font-medium text-ios-green">${Number(customer.creditUsed).toFixed(2)}</span>
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-xs text-ios-gray">
                      {customer._count?.sales || 0}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => openEdit(customer)}
                          className="text-ios-gray hover:text-ios-blue p-1.5 rounded-lg hover:bg-blue-50 transition-colors inline-flex"
                          title="Editar"
                        >
                          <Pencil size={16} />
                        </button>
                        <button
                          onClick={() => handleDeactivate(customer.id, customer.name)}
                          className="text-ios-gray hover:text-ios-red p-1.5 rounded-lg hover:bg-red-50 transition-colors inline-flex"
                          title="Desactivar"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Customer Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[20px] w-full max-w-md shadow-ios-xl">
            <div className="flex items-center justify-between p-4 border-b border-ios-gray-5">
              <h3 className="font-semibold">{editingId ? 'Editar Cliente' : 'Nuevo Cliente'}</h3>
              <button onClick={closeForm} className="p-2 rounded-lg hover:bg-ios-gray-6">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-xs text-ios-gray mb-1 block">Nombre *</label>
                <input type="text" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Nombre completo" className="ios-input w-full" autoFocus />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-ios-gray mb-1 block">Teléfono</label>
                  <input type="tel" value={form.phone} onChange={(e) => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="+52..." className="ios-input w-full" />
                </div>
                <div>
                  <label className="text-xs text-ios-gray mb-1 block">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))} placeholder="correo@email.com" className="ios-input w-full" />
                </div>
              </div>
              <div>
                <label className="text-xs text-ios-gray mb-1 block">Dirección</label>
                <input type="text" value={form.address} onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))} placeholder="Calle, número, colonia" className="ios-input w-full" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-ios-gray mb-1 block">RFC</label>
                  <input type="text" value={form.rfc} onChange={(e) => setForm(p => ({ ...p, rfc: e.target.value }))} placeholder="RFC" className="ios-input w-full" />
                </div>
                <div>
                  <label className="text-xs text-ios-gray mb-1 block">Límite de crédito</label>
                  <input type="number" value={form.creditLimit} onChange={(e) => setForm(p => ({ ...p, creditLimit: parseFloat(e.target.value) || 0 }))} placeholder="0.00" className="ios-input w-full" step="0.01" />
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-ios-gray-5 flex gap-2">
              <button onClick={closeForm} className="flex-1 px-4 py-2.5 rounded-[10px] text-sm border border-ios-gray-5 hover:bg-ios-gray-6">
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving || !form.name.trim()}
                className="flex-1 px-4 py-2.5 rounded-[10px] text-sm bg-ios-blue text-white font-medium hover:bg-ios-blue-600 disabled:opacity-50"
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
      {showImportModal && (
        <ImportCustomersModal
          onClose={() => setShowImportModal(false)}
          onSuccess={() => {
            setShowImportModal(false);
            loadCustomers();
          }}
        />
      )}
      </>
      )}
    </div>
  );
}
